// The certificate poll's cadence, in one place because two files need to agree on
// it: classroom-v2.middleware.ts runs the loop, and certificate-step.tsx decides
// when a wait has gone on long enough to say something about. Previously the
// component guessed at that from an attempt-count landmark it kept in sync by hand,
// which is exactly the kind of duplicated arithmetic that drifts.
//
// Fast at first because the common case resolves in a few seconds, then backing off
// so a genuinely slow Lambda doesn't turn into hundreds of requests.
export let CERTIFICATE_POLL_FAST_INTERVAL_MS = 2_000
export let CERTIFICATE_POLL_SLOW_INTERVAL_MS = 5_000

// ~30s at the fast cadence, per the contract's suggestion, then back off.
export let CERTIFICATE_POLL_FAST_ATTEMPTS = 15

// Where the loop stops rather than spinning forever. A certificate this slow means
// something went wrong upstream that no amount of further asking will fix, and the
// backend logs CertificateClaimDispatched / CertificateClaimContextMissing for
// tracing it. Works out to ~75s of waiting at the cadence above.
export let CERTIFICATE_POLL_MAX_ATTEMPTS = 24

// How long to wait before the next attempt.
export let certificatePollDelayMs = (attempt: number): number =>
  attempt < CERTIFICATE_POLL_FAST_ATTEMPTS
    ? CERTIFICATE_POLL_FAST_INTERVAL_MS
    : CERTIFICATE_POLL_SLOW_INTERVAL_MS

// Roughly how long this poll has been running, summed from the cadence rather than
// clocked, so it survives re-renders and needs no timer of its own.
//
// This measures THIS POLL, deliberately not the certificate's age. The obvious
// alternative — the server's requirementsCompletedTime — is the wrong clock: it's
// when the learner finished their requirements, which on any re-entry is already
// minutes or days in the past, so a returning learner would be shown "this is
// taking longer than usual" for a certificate that has been generating for two
// seconds.
export let certificatePollElapsedMs = (attempts: number): number =>
  Array.from({ length: Math.max(0, attempts - 1) }, (_, i) =>
    certificatePollDelayMs(i + 1),
  ).reduce((total, delay) => total + delay, 0)

// The point at which a wait stops being "nearly there" and becomes worth telling
// the learner about. Under this, a spinner is the honest answer and any commentary
// would only invite them to think something is wrong.
export let CERTIFICATE_SLOW_THRESHOLD_MS = 45_000

export let isCertificateGenerationSlow = (attempts: number): boolean =>
  certificatePollElapsedMs(attempts) >= CERTIFICATE_SLOW_THRESHOLD_MS
